import React, { useEffect, useMemo } from 'react';
import { Button, Select, Space } from 'antd';
import { CloseOutlined, ArrowLeftOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { aiCohortDrawerAtom, selectedProductAtom } from '../SensitivityControl/atoms';
import { Retry } from '@src/components/common';
import AIDimensions from '@src/components/ai-cohorts';
import { isConfigLoadingAtom, highlightDimensionAtom } from '@src/components/ai-cohorts/atoms';
import {
  StyledDrawer,
  ContentWrapper,
  StyledCloseButton,
  SelectGroupLabel,
  InfoText,
} from './styled';
import AICohortHeader from './Header';
import { useFetchConfigList } from './useFetchMetricConfig';
import { DPI_DEFAULT_AI_COHORT_METRIC } from '../../constants';
import { isAppModule, PRODUCT_MODE_MAP } from '@src/utils/constants';
import { useGetAIAlertMetricsCategories } from '../../hooks/useAIAlertMetricsCategory';
import useAIAlertMetricsMeta from '../../hooks/useAIAlertMetricsMeta';
import { DefaultOptionType } from 'antd/es/select';

export interface AICohortDrawerProps {
  visible: boolean;
  onClose: () => void;
  sensitivityConfigDrawerOpen: boolean;
  savedSensitivityList?: any[];
}

const AICohortDrawer: React.FC<AICohortDrawerProps> = ({
  visible,
  onClose,
  sensitivityConfigDrawerOpen,
}) => {
  const {
    aiAlertMetricsMeta = [],
    isFetching: isSensitivityFetching,
    isError: isSensitivityError,
    refetch: sensitivityRefetch,
  } = useAIAlertMetricsMeta(visible && isAppModule, []);

  const { categoryMap } = useGetAIAlertMetricsCategories({
    aiAlertMetricsMeta: aiAlertMetricsMeta,
    searchText: '',
    product: PRODUCT_MODE_MAP.APP,
    withOneFlowCategory: true,
  });

  const selectedProduct = useAtomValue(selectedProductAtom);
  const [aiCohortDrawerConfig, setAICohortDrawerConfig] = useAtom(aiCohortDrawerAtom);
  const { data, isLoading, isFetching, refetch, isError } =
    useFetchConfigList(aiCohortDrawerConfig);
  const configList = data ?? {
    aiAlertsConfig: [],
    customerRootCauseType: null,
  };

  const [configLoading, setConfigLoading] = useAtom(isConfigLoadingAtom);
  const setHighlightDimension = useSetAtom(highlightDimensionAtom);

  useEffect(() => {
    setConfigLoading(isFetching);
  }, [isFetching, setConfigLoading]);

  const handleClose = () => {
    setHighlightDimension([]);
    onClose();
    setAICohortDrawerConfig({ open: false, metrics: [], key: '', isFlow: false });
  };

  const options = useMemo(() => {
    let selectOptions: DefaultOptionType[] = [];

    selectOptions = categoryMap.map<DefaultOptionType>((category) => {
      return {
        label: <SelectGroupLabel>{category.key}</SelectGroupLabel>,
        value: category.key,
        options: category.metrics
          .sort((a, b) => a.displayName.localeCompare(b.displayName))
          .map((metric) => {
            return {
              label: metric.displayName,
              value: metric.name,
              metrics: category.isFlow ? metric.metrics : [metric.name],
              isFlow: category.isFlow,
            };
          }),
      };
    });

    return selectOptions;
  }, [categoryMap]);

  const handleChange = (newMetric, option) => {
    setAICohortDrawerConfig({
      ...aiCohortDrawerConfig,
      metrics: option.metrics,
      key: newMetric,
      isFlow: option.isFlow,
    });
  };

  const footerButtons = [
    <Space key="ai-cohort-footer">
      <Button onClick={handleClose}>Back</Button>
    </Space>,
  ];

  return (
    <StyledDrawer
      title={<AICohortHeader selectedProduct={selectedProduct} data={configList} />}
      width={window.innerWidth > 1200 ? 1200 : window.innerWidth - 100}
      open={visible}
      onClose={handleClose}
      footer={footerButtons}
      forceRender={true}
      // mask={true}
      maskClosable={true}
      keyboard={true}
      closeIcon={
        <StyledCloseButton
          type="text"
          icon={sensitivityConfigDrawerOpen ? <ArrowLeftOutlined /> : <CloseOutlined />}
          onClick={handleClose}
        >
          {sensitivityConfigDrawerOpen ? 'Back to Sensitivity' : ''}
        </StyledCloseButton>
      }
    >
      <ContentWrapper>
        <InfoText>
          <ExclamationCircleOutlined style={{ color: '#0046ff', marginRight: '4px' }} />
          Add Dimensions that matter to you for AI Alert Root Cause. And remove the ones which are
          noisy.
        </InfoText>
        {isAppModule ? (
          <Select
            showSearch
            optionFilterProp="label"
            size={'large'}
            style={{
              fontSize: '20px',
              width: 'max-content',
              paddingInlineStart: '40px',
              margin: '10px 0 10px 0',
            }}
            value={aiCohortDrawerConfig.key || DPI_DEFAULT_AI_COHORT_METRIC}
            styles={{
              popup: {
                root: {
                  minWidth: '300px',
                  maxWidth: '500px',
                  width: 'fit-content',
                  padding: '10px 5px',
                },
              },
            }}
            options={options}
            onChange={handleChange}
          />
        ) : null}
        <Retry
          isLoading={isLoading || isFetching || configLoading || isSensitivityFetching}
          fetchData={() => {
            if (isError) refetch();
            if (isSensitivityError) sensitivityRefetch();
          }}
          showError={isError || isSensitivityError}
          mountChildAlways={false}
        >
          <AIDimensions isLoading={false} isFetching={false} data={configList} />
        </Retry>
      </ContentWrapper>
    </StyledDrawer>
  );
};

export default AICohortDrawer;
